import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { UserCircleIcon, ShieldCheckIcon, KeyIcon, ClockIcon, ChartBarIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiClient from '../services/apiClient';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const AccountSettingsPage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    company: user?.company || '',
    phone: user?.phone || ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    otp: ''
  });
  
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Update profile data when user changes
  React.useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        company: user.company || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  // Generate user display ID (consistent with header)
  const generateUserID = () => {
    if (!user?.email) return 'USR12345';
    const hash = user.email.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return `USR${Math.abs(hash).toString().substring(0, 5)}`;
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserCircleIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
  ];

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      const response = await updateUser(profileData);
      if (response.success) {
        toast.success('Profile updated successfully!');
      } else {
        toast.error(response.error || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Error updating profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    // Validate password fields before requesting OTP
    if (!passwordData.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    
    if (!passwordData.newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    // Additional password strength checks
    if (passwordData.newPassword === passwordData.currentPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setOtpLoading(true);
    try {
      await apiClient.post('/auth/request-password-change-otp');
      toast.success('Verification code sent to your email');
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to send verification code');
      console.error('Error requesting OTP:', error);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!otpSent) {
      toast.error('Please request a verification code first');
      return;
    }
    
    if (!passwordData.otp) {
      toast.error('Please enter the verification code');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiClient.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        otp: passwordData.otp
      });
      
      if (response.success) {
        toast.success('Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '', otp: '' });
        setOtpSent(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to change password');
      console.error('Error changing password:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-8" style={{ backgroundColor: '#197560' }}>
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                <svg className="w-8 h-8" style={{ color: '#197560' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
            </div>
            <div className="text-white">
              <h1 className="text-2xl font-bold">{user?.firstName} {user?.lastName}</h1>
              <p className="text-green-100">{user?.email}</p>
              <p className="text-green-200 text-sm font-medium">ID: {generateUserID()}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'text-white shadow-lg'
                    : 'border-transparent text-gray-500 hover:text-green-600 hover:border-green-300'
                }`}
                style={{
                  borderBottomColor: activeTab === tab.id ? '#197560' : undefined,
                  color: activeTab === tab.id ? '#197560' : undefined
                }}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="mt-1 bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={profileData.company}
                    onChange={(e) => setProfileData({...profileData, company: e.target.value})}
                    className="mt-1"
                    placeholder="Your company name"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="phone">Phone Number (optional)</Label>
                  <PhoneInput
                    country={'in'}
                    value={profileData.phone}
                    onChange={(phone) => setProfileData(prev => ({ ...prev, phone: phone }))}
                    enableSearch={true}
                    searchPlaceholder="Search country"
                    inputProps={{
                      name: 'phone',
                      required: false
                    }}
                    containerStyle={{
                      width: '100%',
                      marginTop: '0.25rem'
                    }}
                    inputStyle={{
                      width: '100%',
                      height: '42px',
                      fontSize: '14px',
                      paddingLeft: '48px',
                      borderRadius: '0.375rem',
                      border: '1px solid #d1d5db',
                      backgroundColor: '#ffffff'
                    }}
                    buttonStyle={{
                      borderRadius: '0.375rem 0 0 0.375rem',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #d1d5db',
                      borderRight: 'none'
                    }}
                    dropdownStyle={{
                      borderRadius: '0.375rem'
                    }}
                  />
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  className="px-6"
                  style={{ backgroundColor: '#197560' }}
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Two-Factor Authentication - Featured Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-blue-600 rounded-xl shadow-md">
                      <KeyIcon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 mb-1">Two-Factor Authentication</h2>
                      <p className="text-sm text-gray-700">
                        Add an extra layer of security to your account with 2FA. Highly recommended for all users.
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/dashboard/two-factor')}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                  style={{ backgroundColor: '#197560' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#155a4d'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#197560'}
                >
                  <span>Configure Two-Factor Authentication</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Other Security Features */}
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <div 
                  onClick={() => navigate('/dashboard/security')}
                  className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:border-green-500 hover:shadow-md transition-all duration-200 cursor-pointer group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-500 transition-colors duration-200">
                        <ChartBarIcon className="w-6 h-6 text-purple-600 group-hover:text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Security Dashboard</h3>
                        <p className="text-sm text-gray-600">Monitor sessions, login history & security events</p>
                      </div>
                    </div>
                    <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-purple-600 transition-colors duration-200" />
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
                
                <div>
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    className="mt-1"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    className={`mt-1 ${
                      passwordData.newPassword && passwordData.newPassword.length < 8 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                        : ''
                    }`}
                    placeholder="Enter new password (min. 8 characters)"
                  />
                  {passwordData.newPassword && passwordData.newPassword.length < 8 && (
                    <p className="text-xs text-red-600 mt-1">Password must be at least 8 characters long</p>
                  )}
                  {passwordData.newPassword && passwordData.newPassword.length >= 8 && (
                    <p className="text-xs text-green-600 mt-1">✓ Password length is valid</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    className={`mt-1 ${
                      passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword
                        ? 'border-green-300 focus:border-green-500 focus:ring-green-500'
                        : ''
                    }`}
                    placeholder="Confirm new password"
                  />
                  {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                    <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                  )}
                  {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && passwordData.confirmPassword.length >= 8 && (
                    <p className="text-xs text-green-600 mt-1">✓ Passwords match</p>
                  )}
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <Label htmlFor="otp">Verification Code</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="otp"
                      type="text"
                      value={passwordData.otp}
                      onChange={(e) => setPasswordData({...passwordData, otp: e.target.value})}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      disabled={!otpSent}
                    />
                    <Button
                      onClick={handleRequestOTP}
                      disabled={
                        otpLoading || 
                        !passwordData.currentPassword || 
                        !passwordData.newPassword || 
                        !passwordData.confirmPassword ||
                        passwordData.newPassword.length < 8 ||
                        passwordData.newPassword !== passwordData.confirmPassword
                      }
                      variant="outline"
                      className="whitespace-nowrap"
                    >
                      {otpLoading ? 'Sending...' : otpSent ? 'Resend Code' : 'Send Code'}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {otpSent ? 'Code sent to your email. Check your inbox.' : 'Fill in all password fields to request verification code'}
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button
                  onClick={handleChangePassword}
                  disabled={isLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.otp}
                  className="px-6"
                  style={{ backgroundColor: '#197560' }}
                >
                  {isLoading ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;